/**
 * 线性零部件阵列命令生命周期。
 */
export enum LinearComponentPatternLifecycle {
  /** 命令未打开。 */
  Closed = 'closed',
  /** 命令已打开并等待用户输入。 */
  Open = 'open',
}

/**
 * 线性零部件阵列选择槽位。
 */
export enum LinearComponentPatternSelectionSlot {
  /** 方向 1 选择槽位，对应"阵列方向"选择列表。 */
  Direction1Reference = 'direction-1-reference',

  /** 方向 1 旋转实例的旋转轴选择槽位。 */
  Direction1RotationAxis = 'direction-1-rotation-axis',

  /** 方向 1 到参考模式的"参考几何"选择槽位。 */
  Direction1ReferenceGeometry = 'direction-1-reference-geometry',
  /** 方向 1 到参考模式的"所选参考"选择槽位。 */
  Direction1SelectedReference = 'direction-1-selected-reference',
  /** 方向 2 选择槽位，对应"阵列方向"选择列表。 */
  Direction2Reference = 'direction-2-reference',
  /** 方向 2 到参考模式的"参考几何"选择槽位。 */
  Direction2ReferenceGeometry = 'direction-2-reference-geometry',
  /** 方向 2 到参考模式的"所选参考"选择槽位。 */
  Direction2SelectedReference = 'direction-2-selected-reference',
  /** 要阵列的零部件。 */
  ComponentsToPattern = 'components-to-pattern',
  /** 跳过的实例。 */
  SkippedInstances = 'skipped-instances',
  /** 修改的实例。 */
  ModifiedInstances = 'modified-instances',
}

/** 必须提供可解析方向的槽位；组件及到参考槽不共享此限制。 */
export const LINEAR_COMPONENT_PATTERN_DIRECTION_REFERENCE_SLOTS: readonly LinearComponentPatternSelectionSlot[] = Object.freeze([
  LinearComponentPatternSelectionSlot.Direction1Reference,
  LinearComponentPatternSelectionSlot.Direction1RotationAxis,
  LinearComponentPatternSelectionSlot.Direction2Reference,
]);

/**
 * 线性零部件阵列方向定义方式。
 */
export enum LinearComponentPatternDirectionMode {
  /** 通过间距和实例数定义阵列方向。 */
  SpacingAndInstances = 'spacing-and-instances',
  /** 通过到参考规则定义阵列方向。 */
  UpToReference = 'up-to-reference',
}

/**
 * 到参考模式的偏移参考类型。
 */
export enum LinearComponentPatternReferenceOffsetMode {
  /** 使用零部件包围盒中心作为起算参考。 */
  BoundingBoxCenter = 'bounding-box-center',
  /** 使用用户选中的源几何作为起算参考。 */
  SelectedReference = 'selected-reference',
}


/**
 * 旋转实例对齐到源时使用的参考点。
 */
export enum LinearComponentPatternRotationReferencePoint {
  /** 使用零部件包围盒中心作为旋转定位参考。 */
  BoundingBoxCenter = 'bounding-box-center',
  /** 使用零部件原点作为旋转定位参考。 */
  ComponentOrigin = 'component-origin',
}



/**
 * 线性零部件阵列引用描述符类型。
 */
export enum LinearComponentPatternReferenceDescriptorKind {
  /** 稳定存在于场景树中的 Object3D 引用。 */
  SceneObject = 'scene-object',
  /** 由拓扑宿主结果体按稳定索引重建的特征点线面引用。 */
  TopologyProxy = 'topology-proxy',
}

/**
 * 线性零部件阵列拓扑代理引用类型。
 */
export enum LinearComponentPatternTopologyProxyKind {
  /** 特征点代理。 */
  Vertex = 'vertex',
  /** 特征线代理。 */
  Edge = 'edge',
  /** 特征面代理。 */
  Face = 'face',
}

/**
 * 场景对象引用描述符。
 */
export interface LinearComponentPatternSceneObjectReferenceDescriptor {
  /** 描述符类型。 */
  kind: LinearComponentPatternReferenceDescriptorKind.SceneObject;
  /** 场景对象 ID，优先对应 Object3D.uuid，兼容 Object3D.name。 */
  objectId: string;
}

/**
 * 拓扑代理引用描述符。
 *
 * 设计说明：
 * 1. FeaturePoint / FeatureLine / FeatureFace 是按拾取动态创建的代理对象，uuid 不可作为特征定义；
 * 2. imported BRep 的 pick host 也是动态对象，必须优先保存装配拓扑稳定引用；
 * 3. hostObjectId 只作为原生结果体或旧定义兼容兜底，不能作为 imported BRep 的稳定真相。
 */
export interface LinearComponentPatternTopologyProxyReferenceDescriptor {
  /** 描述符类型。 */
  kind: LinearComponentPatternReferenceDescriptorKind.TopologyProxy;
  /** 拓扑代理类型。 */
  topologyKind: LinearComponentPatternTopologyProxyKind;
  /** 拓扑宿主结果体对象 ID。 */
  hostObjectId: string;
  /** 拓扑索引。 */
  topologyIndex: number;
  /** 持久化拓扑引用。 */
  persistentRef: string;
  /** imported BRep 装配拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;
}

/**
 * 线性零部件阵列引用描述符。
 */
export type LinearComponentPatternReferenceDescriptor =
  | LinearComponentPatternSceneObjectReferenceDescriptor
  | LinearComponentPatternTopologyProxyReferenceDescriptor;


/**
 * 单个方向的求解状态。
 */
export interface LinearComponentPatternDirectionDefinitionState {
  /** 当前方向定义方式。 */
  mode: LinearComponentPatternDirectionMode;
  /** 当前方向间距（米制渲染空间单位）。 */
  spacing: number;
  /** 当前方向实例数（含源实例）。 */
  instances: number;
  /** 到参考模式的末端偏移距离（米制渲染空间单位）。 */
  offsetDistance: number;
  /** 到参考模式的起算参考类型。 */
  referenceOffsetMode: LinearComponentPatternReferenceOffsetMode;
  /** 到参考模式是否由间距驱动。 */
  setSpacing: boolean;
  /** 到参考模式是否由实例数驱动。 */
  setInstances: boolean;

    /** 是否反转当前阵列方向。 */
  reverseDirection?: boolean;


    /** 方向 2 是否只复制源零部件。 */
  patternSeedOnly?: boolean;


    /** 是否启用旋转实例。 */
  rotateInstances?: boolean;
  /** 旋转实例角度增量（角度制）。 */
  rotationAngle?: number;
  /** 是否反转旋转方向。 */
  reverseRotation?: boolean;
  /**
   * 是否固定旋转轴。
   *
   * - `true`（默认）：所有阵列实例绕同一条公共旋转轴旋转；
   * - `false`：每个实例的旋转轴沿方向 1 平移后再旋转。
   */
  fixedRotationAxis?: boolean;
  /** 是否对齐每个实例以匹配源零部件原有朝向。 */
  alignToSeed?: boolean;
  /** 对齐到源时使用的参考点。 */
  rotationReferencePoint?: LinearComponentPatternRotationReferencePoint;

}

/**
 * 线性零部件阵列预览对象角色。
 */
export enum LinearComponentPatternPreviewObjectRole {
  /** 逻辑实例组。 */
  InstanceGroup = 'instance-group',
  /** 跳过实例选择小球。 */
  SkipInstanceMarker = 'skip-instance-marker',
}

/**
 * 线性零部件阵列逻辑实例描述。
 */
export interface LinearComponentPatternPreviewInstanceDescriptor {
  /** 逻辑实例稳定键。 */
  instanceKey: string;
  /** 方向 1 序号。 */
  direction1Index: number;
  /** 方向 2 序号。 */
  direction2Index: number;
}

/**
 * 线性零部件阵列跳过小球元数据。
 */
export interface LinearComponentPatternPreviewMarkerMetadata
  extends LinearComponentPatternPreviewInstanceDescriptor {
  /** 预览对象角色。 */
  role: LinearComponentPatternPreviewObjectRole.SkipInstanceMarker;

    /** 小球对应的源零部件对象 ID。 */
  sourceObjectId: string;

}

/**
 * 创建线性零部件阵列逻辑实例稳定键。
 *
 * @param direction1Index 方向 1 序号。
 * @param direction2Index 方向 2 序号。
 * @returns 逻辑实例稳定键。
 */
export function createLinearComponentPatternInstanceKey(
  direction1Index: number,
  direction2Index: number,
): string {
  return `${direction1Index}:${direction2Index}`;
}

/**
 * 线性零部件阵列状态快照。
 */
export interface LinearComponentPatternState {
  /** 命令生命周期。 */
  lifecycle: LinearComponentPatternLifecycle;
  /** 当前激活选择槽位。 */
  activeSelectionSlot: LinearComponentPatternSelectionSlot;
  /** 方向 1 参考对象 ID。 */
  direction1ReferenceObjectId: string | null;
  /** 方向 1 参考显示名称。 */
  direction1ReferenceDisplayName: string;

    /** 方向 1 参考的稳定引用描述符。 */
  direction1ReferenceDescriptor: LinearComponentPatternReferenceDescriptor | null;


    /** 方向 1 旋转实例的旋转轴对象 ID。 */
  direction1RotationAxisObjectId: string | null;
  /** 方向 1 旋转实例的旋转轴显示名称。 */
  direction1RotationAxisDisplayName: string;

    /** 方向 1 旋转轴的稳定引用描述符。 */
  direction1RotationAxisDescriptor: LinearComponentPatternReferenceDescriptor | null;

    /** 方向 1 是否启用旋转实例。 */
  direction1RotateInstances: boolean;
  /** 方向 1 旋转实例角度增量（角度制）。 */
  direction1RotationAngle: number;
  /** 方向 1 是否反转旋转实例方向。 */
  direction1ReverseRotation: boolean;
  /**
   * 方向 1 是否固定旋转轴。
   *
   * - `true`（默认）：所有阵列实例绕同一条公共旋转轴旋转；
   * - `false`：每个实例的旋转轴沿方向 1 平移后再旋转。
   */
  direction1FixedRotationAxis: boolean;
  /** 方向 1 旋转实例是否对齐到源。 */
  direction1AlignToSeed: boolean;
  /** 方向 1 旋转实例对齐到源时使用的参考点。 */
  direction1RotationReferencePoint: LinearComponentPatternRotationReferencePoint;

    /** 方向 1 到参考模式的参考几何对象 ID。 */
  direction1ReferenceGeometryObjectId: string | null;
  /** 方向 1 到参考模式的参考几何显示名称。 */
  direction1ReferenceGeometryDisplayName: string;

    /** 方向 1 到参考模式参考几何的稳定引用描述符。 */
  direction1ReferenceGeometryDescriptor: LinearComponentPatternReferenceDescriptor | null;

    /** 方向 1 到参考模式的所选参考对象 ID。 */
  direction1SelectedReferenceObjectId: string | null;
  /** 方向 1 到参考模式的所选参考显示名称。 */
  direction1SelectedReferenceDisplayName: string;

    /** 方向 1 到参考模式所选参考的稳定引用描述符。 */
  direction1SelectedReferenceDescriptor: LinearComponentPatternReferenceDescriptor | null;

    /** 方向 1 当前定义方式。 */
  direction1Mode: LinearComponentPatternDirectionMode;
  /** 方向 1 间距（米制渲染空间单位）。 */
  direction1Spacing: number;
  /** 方向 1 实例数（含源实例）。 */
  direction1Instances: number;
  /** 方向 1 到参考模式的末端偏移距离（米制渲染空间单位）。 */
  direction1OffsetDistance: number;
  /** 方向 1 到参考模式的起算参考类型。 */
  direction1ReferenceOffsetMode: LinearComponentPatternReferenceOffsetMode;
  /** 方向 1 到参考模式是否由间距驱动。 */
  direction1SetSpacing: boolean;
  /** 方向 1 到参考模式是否由实例数驱动。 */
  direction1SetInstances: boolean;

    /** 方向 1 是否反转阵列传播方向。 */
  direction1ReverseDirection: boolean;

    /** 方向 2 参考对象 ID。 */
  direction2ReferenceObjectId: string | null;
  /** 方向 2 参考显示名称。 */
  direction2ReferenceDisplayName: string;

    /** 方向 2 参考的稳定引用描述符。 */
  direction2ReferenceDescriptor: LinearComponentPatternReferenceDescriptor | null;

    /** 方向 2 到参考模式的参考几何对象 ID。 */
  direction2ReferenceGeometryObjectId: string | null;
  /** 方向 2 到参考模式的参考几何显示名称。 */
  direction2ReferenceGeometryDisplayName: string;

    /** 方向 2 到参考模式参考几何的稳定引用描述符。 */
  direction2ReferenceGeometryDescriptor: LinearComponentPatternReferenceDescriptor | null;

    /** 方向 2 到参考模式的所选参考对象 ID。 */
  direction2SelectedReferenceObjectId: string | null;
  /** 方向 2 到参考模式的所选参考显示名称。 */
  direction2SelectedReferenceDisplayName: string;

    /** 方向 2 到参考模式所选参考的稳定引用描述符。 */
  direction2SelectedReferenceDescriptor: LinearComponentPatternReferenceDescriptor | null;

    /** 方向 2 当前定义方式。 */
  direction2Mode: LinearComponentPatternDirectionMode;
  /** 方向 2 间距（米制渲染空间单位）。 */
  direction2Spacing: number;
  /** 方向 2 实例数（含源实例）。 */
  direction2Instances: number;
  /** 方向 2 到参考模式的末端偏移距离（米制渲染空间单位）。 */
  direction2OffsetDistance: number;
  /** 方向 2 到参考模式的起算参考类型。 */
  direction2ReferenceOffsetMode: LinearComponentPatternReferenceOffsetMode;
  /** 方向 2 到参考模式是否由间距驱动。 */
  direction2SetSpacing: boolean;
  /** 方向 2 到参考模式是否由实例数驱动。 */
  direction2SetInstances: boolean;

    /** 方向 2 是否反转阵列传播方向。 */
  direction2ReverseDirection: boolean;


    /** 方向 2 是否只复制源零部件。 */
  direction2PatternSeedOnly: boolean;

    /** 要阵列的零部件对象 ID 列表。 */
  componentObjectIds: string[];
  /** 跳过实例逻辑键列表。 */
  skippedInstanceKeys: string[];
  /** 修改实例对象 ID 列表。 */
  modifiedInstanceObjectIds: string[];
}


/**
 * 线性零部件阵列定义快照。
 *
 * 设计说明：
 * 1. 已提交阵列需要能从特征树重新进入 PropertyManager，因此必须保存创建时的完整参数；
 * 2. 快照只保存可序列化的稳定描述符、ID、数值和枚举，不保存 Three.js 对象，避免把运行时对象泄漏到主状态；
 * 3. 编辑特征时由控制器按稳定描述符重建点线面代理，预览和提交仍复用创建链路。
 */
export interface LinearComponentPatternDefinition {
  /** 阵列命令参数状态快照。 */
  state: LinearComponentPatternState;
}


/**
 * 线性零部件阵列 ViewModel。
 */
export interface LinearComponentPatternViewModel {
  /** 当前状态快照。 */
  state: LinearComponentPatternState;
}

/**
 * 线性零部件阵列选择变化载荷。
 */
export interface LinearComponentPatternSelectionChangePayload {
  /** 当前激活选择槽位。 */
  activeSelectionSlot: LinearComponentPatternSelectionSlot;
  /** 选择对象 ID（引用类槽位使用）。 */
  objectId: string | null;
  /** 选择对象显示名称（引用类槽位使用）。 */
  displayName: string;
  /**
   * 已选零部件对象 ID 列表（ComponentsToPattern 槽位使用）。
   *
   * 说明：组件槽支持多选，此字段携带完整列表供 UI 直接同步，
   * 避免 UI 轮询 ViewModel 产生延迟。
   */
  componentObjectIds?: readonly string[];
  /**
   * 已跳过逻辑实例键列表（SkippedInstances 槽位使用）。
   *
   * 说明：跳过实例基于逻辑实例位置，而不是临时预览对象 uuid。
   */
  skippedInstanceKeys?: readonly string[];
}

/**
 * 线性零部件阵列选择变化监听器。
 */
export type LinearComponentPatternSelectionChangeListener = (
  payload: LinearComponentPatternSelectionChangePayload,
) => void;

/**
 * 线性零部件阵列单个实例提交数据。
 */
export interface LinearComponentPatternInstanceCommitData {
  /** 源零部件对象 ID。 */
  sourceObjectId: string;
  /** 源对象基础上的世界偏移。 */
  worldOffset: {
    /** 世界 X 偏移。 */
    x: number;
    /** 世界 Y 偏移。 */
    y: number;
    /** 世界 Z 偏移。 */
    z: number;
  };

    /** 源对象基础上的世界变换；存在时优先于 `worldOffset` 使用。 */
  worldTransform?: {
    /** 世界位置。 */
    position: {
      /** 世界 X 坐标。 */
      x: number;
      /** 世界 Y 坐标。 */
      y: number;
      /** 世界 Z 坐标。 */
      z: number;
    };
    /** 世界姿态四元数。 */
    quaternion: {
      /** 四元数 X 分量。 */
      x: number;
      /** 四元数 Y 分量。 */
      y: number;
      /** 四元数 Z 分量。 */
      z: number;
      /** 四元数 W 分量。 */
      w: number;
    };
    /** 世界缩放。 */
    scale: {
      /** X 轴缩放。 */
      x: number;
      /** Y 轴缩放。 */
      y: number;
      /** Z 轴缩放。 */
      z: number;
    };
  };

}

/**
 * 线性零部件阵列提交数据。
 */
export interface LinearComponentPatternCommitData {
  /** 本次阵列要落地的实例列表（不包含源实例）。 */
  instances: LinearComponentPatternInstanceCommitData[];
}

/**
 * 线性零部件阵列已提交零件记录。
 */
export interface LinearComponentPatternCommittedPartRecord {
  /** 阵列零件对象 ID（THREE.Object3D uuid）。 */
  objectId: string;
  /** 阵列零件对应的来源零部件对象 ID。 */
  sourceObjectId: string;
  /** 阵列零件在设计树中的显示名称。 */
  displayName: string;
}

/**
 * 线性零部件阵列已提交记录。
 */
export interface LinearComponentPatternCommittedRecord {
  /** 阵列记录 ID。 */
  id: string;
  /** 阵列定义所属的装配 occurrence。 */
  ownerAssemblyNodeId?: string;
  /** 阵列组对象 ID（THREE.Group uuid），用于场景对象与设计树节点的关联。 */
  groupObjectId: string;
  /** 阵列组场景对象名称（内部名称，非显示名称）。 */
  groupName: string;
  /** 阵列组在设计树中的显示名称，如"局部线性阵列1"。 */
  displayName: string;
  /** 本次阵列提交产生的零件节点列表。 */
  partRecords: LinearComponentPatternCommittedPartRecord[];
  /** 实例数量。 */
  instanceCount: number;

    /** 用于历史恢复时重新实体化阵列结果的提交数据快照。 */
  commitData?: LinearComponentPatternCommitData;


    /** 用于从特征树重新编辑该阵列的定义快照。 */
  definition?: LinearComponentPatternDefinition;

    /** 提交时间戳（毫秒）。 */
  createdAt: number;
}


/**
 * 线性零部件阵列管理器固定实体 ID。
 *
 * 设计说明：
 * 1. Manager 是装配体文档下的唯一线性阵列宿主，历史与 JSON 都应使用稳定实体 ID；
 * 2. 禁止把运行时 `Object3D.uuid` 写入长期 JSON，否则重开项目后 managerId 会失去比较意义；
 * 3. 常量集中定义，避免历史 adapter 与 Manager JSON 各自维护字符串。
 */
export const LINEAR_COMPONENT_PATTERN_MANAGER_ENTITY_ID = 'linear-component-pattern-manager';

/**
 * 线性零部件阵列历史快照。
 *
 * 设计说明：
 * 1. 历史系统只保存 plain data，不能保存 Three.js 对象、材质或几何体引用；
 * 2. records 中的 `commitData` 用于恢复阵列实例对象，`definition` 用于恢复编辑特征参数；
 * 3. 整个 manager 作为一个历史实体写入，避免编辑/删除时出现多个阵列记录之间的状态分裂。
 */
export interface LinearComponentPatternHistorySnapshot {
  /** 线性阵列管理器历史实体 ID。 */
  managerId: string;
  /** 已提交阵列记录快照。 */
  records: LinearComponentPatternCommittedRecord[];
}

